
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { createInvitation } from '@/services/invitationService';

const inviteFormSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  role: z.enum(['member', 'manager', 'sales']),
});

type InviteFormValues = z.infer<typeof inviteFormSchema>;

interface SendInviteDialogProps {
  companyId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const SendInviteDialog: React.FC<SendInviteDialogProps> = ({
  companyId,
  open,
  onOpenChange,
  onSuccess
}) => {
  const { toast } = useToast();
  const form = useForm<InviteFormValues>({
    resolver: zodResolver(inviteFormSchema),
    defaultValues: {
      email: '',
      role: 'member' // Changed from 'user' to 'member'
    }
  });

  const onSubmit = async (values: InviteFormValues) => {
    try {
      const result = await createInvitation({
        companyId,
        email: values.email,
        role: values.role,
      });

      if ((result as any).duplicate) {
        toast({
          title: "Invitation already pending",
          description: `An invitation for ${values.email} is already pending.`,
        });
        // Optionally leave dialog open so user can change email
        return;
      }

      if (result.emailStatus === 'sent') {
        toast({
          title: "Invitation sent",
          description: `An invitation email has been sent to ${values.email}`,
        });
      } else if (result.emailErrorCode === 'resend_domain_not_verified') {
        toast({
          title: "Invitation created, but email failed",
          description: result.emailErrorMessage + " The user can still accept via the pending invitations list.",
          variant: "destructive",
        });
      } else if (result.emailStatus === 'failed') {
        toast({
          title: "Invitation created, but email failed",
          description: result.emailErrorMessage || "Failed to send email. The user can still accept via the pending invitations list.",
          variant: "destructive",
        });
      } else if (result.emailStatus === 'skipped') {
        // Handled above (duplicate), but keep as safety
        toast({
          title: "Invitation already pending",
          description: `An invitation for ${values.email} is already pending.`,
        });
        return;
      }

      form.reset();
      onOpenChange(false);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create invitation",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite Team Member</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5 px-6 py-5">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email address</FormLabel>
                  <FormControl>
                    <Input placeholder="email@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="z-50 bg-background">
                      <SelectItem value="member">Member</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="sales">Sales</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? "Sending..." : "Send Invitation"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
